import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Profile } from '@/types/parcel/userData/profile.interface';

import {
  checkEnvioFlexAccess,
  checkFalabellaAccess,
  checkFulfillAccess,
  checkMLFulfillAccess,
  checkMLParcelAccess,
  getBalance,
  getProfile,
} from './asyncRequest';

interface ParcelState {
  profile: {
    loading: boolean;
    error: string | null;
    data: Profile | null;
  };
  balance: {
    loading: boolean;
    error: string | null;
    data: number | null;
  };
  fulfillAccess: {
    loading: boolean;
    error: string | null;
    hasAccess: boolean | null;
    checked: boolean;
  };
  mlParcelAccess: {
    hasAccess: boolean;
    checked: boolean;
  };
  envioFlexAccess: {
    hasAccess: boolean;
    checked: boolean;
  };
  mlFulfillAccess: {
    hasAccess: boolean;
    checked: boolean;
  };
  falabellaAccess: {
    hasAccess: boolean;
    checked: boolean;
  };
}

const initialState: ParcelState = {
  profile: {
    loading: false,
    error: null,
    data: null,
  },
  balance: {
    loading: false,
    error: null,
    data: null,
  },
  fulfillAccess: {
    loading: false,
    error: null,
    hasAccess: null,
    checked: false,
  },
  mlParcelAccess: {
    hasAccess: false,
    checked: false,
  },
  envioFlexAccess: {
    hasAccess: false,
    checked: false,
  },
  mlFulfillAccess: {
    hasAccess: false,
    checked: false,
  },
  falabellaAccess: {
    hasAccess: false,
    checked: false,
  },
};

const parcelSlice = createSlice({
  name: 'parcel',
  initialState,
  reducers: {
    setMLParcelAccess: (state, action: PayloadAction<boolean>) => {
      state.mlParcelAccess.hasAccess = action.payload;
      state.mlParcelAccess.checked = true;
    },
    setFalabellaAccess: (state, action: PayloadAction<boolean>) => {
      state.falabellaAccess.hasAccess = action.payload;
      state.falabellaAccess.checked = true;
    },
    setEnvioFlexAccess: (state, action: PayloadAction<boolean>) => {
      state.envioFlexAccess.hasAccess = action.payload;
      state.envioFlexAccess.checked = true;
    },
    setMLFulfillAccess: (state, action: PayloadAction<boolean>) => {
      state.mlFulfillAccess.hasAccess = action.payload;
      state.mlFulfillAccess.checked = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfile.pending, (state) => {
        state.profile.loading = true;
        state.profile.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.profile.loading = false;
        state.profile.data = action.payload.data;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.profile.loading = false;
        state.profile.error = action.payload as string;
      });

    builder
      .addCase(getBalance.pending, (state) => {
        state.balance.loading = true;
        state.balance.error = null;
      })
      .addCase(getBalance.fulfilled, (state, action) => {
        state.balance.loading = false;
        state.balance.data = action.payload.data;
      })
      .addCase(getBalance.rejected, (state, action) => {
        state.balance.loading = false;
        state.balance.error = action.payload as string;
      });

    builder
      .addCase(checkEnvioFlexAccess.fulfilled, (state, action) => {
        state.envioFlexAccess.hasAccess = action.payload;
        state.envioFlexAccess.checked = true;
      })
      .addCase(checkEnvioFlexAccess.rejected, (state) => {
        state.envioFlexAccess.hasAccess = false;
        state.envioFlexAccess.checked = true;
      });

    builder
      .addCase(checkMLFulfillAccess.fulfilled, (state, action) => {
        state.mlFulfillAccess.hasAccess = action.payload;
        state.mlFulfillAccess.checked = true;
      })
      .addCase(checkMLFulfillAccess.rejected, (state) => {
        state.mlFulfillAccess.hasAccess = false;
        state.mlFulfillAccess.checked = true;
      });

    builder
      .addCase(checkMLParcelAccess.fulfilled, (state, action) => {
        state.mlParcelAccess.hasAccess = action.payload;
        state.mlParcelAccess.checked = true;
      })
      .addCase(checkMLParcelAccess.rejected, (state) => {
        state.mlParcelAccess.hasAccess = false;
        state.mlParcelAccess.checked = true;
      });

    builder
      .addCase(checkFalabellaAccess.fulfilled, (state, action) => {
        state.falabellaAccess.hasAccess = action.payload;
        state.falabellaAccess.checked = true;
      })
      .addCase(checkFalabellaAccess.rejected, (state) => {
        state.falabellaAccess.hasAccess = false;
        state.falabellaAccess.checked = true;
      });

    builder
      .addCase(checkFulfillAccess.pending, (state) => {
        state.fulfillAccess.loading = true;
        state.fulfillAccess.error = null;
      })
      .addCase(checkFulfillAccess.fulfilled, (state, action) => {
        state.fulfillAccess.loading = false;
        state.fulfillAccess.hasAccess = action.payload;
        state.fulfillAccess.checked = true;
        state.fulfillAccess.error = null;
      })
      .addCase(checkFulfillAccess.rejected, (state, action) => {
        state.fulfillAccess.loading = false;
        state.fulfillAccess.hasAccess = false;
        state.fulfillAccess.checked = true;
        state.fulfillAccess.error = action.payload as string;
      });
  },
});

export const { setMLParcelAccess, setEnvioFlexAccess, setMLFulfillAccess, setFalabellaAccess } =
  parcelSlice.actions;
export default parcelSlice.reducer;
